import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardTempFileUploadList } from '../../../components/wizard/WizardTempFileUploadList';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import * as attorneyGeneralService from '../../../services/attorneyGeneralCaseRequests';
import type { AttorneyGeneralInlineUpload, AttorneyGeneralWizardDraft } from '../../../types/attorneyGeneralCaseRequest';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { isBriberySubCategory, isOtherCourtType } from './attorneyGeneralConstants';
import { requiresCourtType } from './attorneyGeneralHelpers';
import { agEnterPlaceholder, agSelectPlaceholder } from './attorneyGeneralPlaceholders';
import { validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string; code?: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

export default function AttorneyGeneralStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const lookups = useAttorneyGeneralLookups(
    'step1',
    draft.provinceId,
    draft.districtId,
    draft.courtTypeId,
  );

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(1, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const showHighCourt = isBriberySubCategory(draft.subFileCategoryName);
  const showCourtType = requiresCourtType(draft.hasActionInstituted);
  const showCourtName = draft.hasActionInstituted === 'yes';
  const showCourtOther = showCourtType && isOtherCourtType(draft.courtTypeId, draft.courtTypeName);

  const next = () => {
    if (!validate()) return;
    nav(attorneyGeneralWizardPath(2, draft.draftRequestId));
  };

  return (
    <AttorneyGeneralWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.description}</label>
          <textarea
            id="ag-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}
            value={draft.description}
            onChange={(e) => {
              setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionOfCase}
          />
          <FieldError message={errors.description} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.province}</label>
            <WizardSelect
              id="ag-province"
              hasError={Boolean(errors.provinceId)}
              value={draft.provinceId}
              placeholder={f.selectProvince}
              options={toOpts(lookups.provinces)}
              onChange={(id) => {
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  provinceId: id,
                  provinceName: lookupName(lookups.provinces, id),
                  districtId: '',
                  districtName: '',
                  magistrateCourtId: '',
                  magistrateCourtName: '',
                  highCourtId: '',
                  highCourtName: '',
                  policeStationId: '',
                  policeStationName: '',
                  courtId: '',
                  courtName: '' }));
                clearErr('provinceId');
              }}
            />
            <FieldError message={errors.provinceId} />
          </div>
          <div className="ccr-fg">
            <label>{f.district}</label>
            <WizardSelect
              id="ag-district"
              value={draft.districtId}
              disabled={!draft.provinceId}
              placeholder={f.selectDistrict}
              options={toOpts(lookups.districts)}
              onChange={(id) => {
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  districtId: id,
                  districtName: lookupName(lookups.districts, id),
                  magistrateCourtId: '',
                  magistrateCourtName: '',
                  highCourtId: '',
                  highCourtName: '',
                  policeStationId: '',
                  policeStationName: '',
                  courtId: '',
                  courtName: '' }));
              }}
            />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.caseSource}</label>
          <textarea
            id="ag-caseSource"
            className={inpClass(Boolean(errors.caseSource))}
            rows={3}
            value={draft.caseSource}
            onChange={(e) => {
              setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, caseSource: maxLen('caseSource', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.sourceOfCase}
          />
          <FieldError message={errors.caseSource} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.magistrateCourtName}</label>
            <WizardSelect
              id="ag-magistrateCourt"
              value={draft.magistrateCourtId}
              disabled={!draft.districtId}
              placeholder={agSelectPlaceholder('Magistrate Court Name')}
              options={toOpts(lookups.magistrateCourts)}
              onChange={(id) =>
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  magistrateCourtId: id,
                  magistrateCourtName: lookupName(lookups.magistrateCourts, id) }))
              }
            />
          </div>
          {showHighCourt ? (
            <div className="ccr-fg">
              <label>{f.highCourtName}</label>
              <WizardSelect
                id="ag-highCourt"
                value={draft.highCourtId}
                disabled={!draft.districtId}
                placeholder={agSelectPlaceholder('High Court Name')}
                options={toOpts(lookups.highCourts)}
                onChange={(id) =>
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    highCourtId: id,
                    highCourtName: lookupName(lookups.highCourts, id) }))
                }
              />
            </div>
          ) : null}
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.policeStation}</label>
            <WizardSelect
              id="ag-policeStation"
              value={draft.policeStationId}
              disabled={!draft.districtId}
              placeholder={agSelectPlaceholder('Police Station')}
              options={toOpts(lookups.policeStations)}
              onChange={(id) =>
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  policeStationId: id,
                  policeStationName: lookupName(lookups.policeStations, id) }))
              }
            />
          </div>
          <div className="ccr-fg">
            <label>{f.countryInvolved}</label>
            <WizardSelect
              id="ag-countryInvolved"
              value={draft.countryInvolvedId}
              placeholder={f.selectCountry}
              options={toOpts(lookups.countries)}
              onChange={(id) =>
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  countryInvolvedId: id,
                  countryInvolvedName: lookupName(lookups.countries, id) }))
              }
            />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.directionOfRequest}</label>
          <textarea
            id="ag-directionOfRequest"
            className={inpClass(Boolean(errors.directionOfRequest))}
            rows={3}
            value={draft.directionOfRequest}
            onChange={(e) => {
              setDraft((d: AttorneyGeneralWizardDraft) => ({
                ...d,
                directionOfRequest: maxLen('directionOfRequest', e.target.value, MAX_REF) }));
            }}
            placeholder={agEnterPlaceholder('Direction of Request')}
          />
          <FieldError message={errors.directionOfRequest} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.yourReferenceNumber}</label>
            <input
              id="ag-yourRef"
              className={inpClass(Boolean(errors.yourReferenceNumber))}
              type="text"
              value={draft.yourReferenceNumber}
              onChange={(e) => {
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  yourReferenceNumber: maxLen('yourReferenceNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.enterExternalReferenceNumber}
            />
            <FieldError message={errors.yourReferenceNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.otherExternalReferences}</label>
            <input
              className={inpClass(Boolean(errors.otherExternalReferences))}
              type="text"
              value={draft.otherExternalReferences}
              onChange={(e) =>
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  otherExternalReferences: maxLen('otherExternalReferences', e.target.value, MAX_REF) }))
              }
              placeholder={f.commaSeparatedReferenceNumbers}
            />
            <FieldError message={errors.otherExternalReferences} />
          </div>
        </div>

        <div className="ccr-fg" id="ag-previousAdvice" style={{ marginBottom: 14 }}>
          <label>{f.hasPreviousAdvice}</label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="previousAdvice"
                checked={draft.hasPreviousAdvice === 'yes'}
                onChange={() => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, hasPreviousAdvice: 'yes' }));
                  clearErr('hasPreviousAdvice');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="previousAdvice"
                checked={draft.hasPreviousAdvice === 'no'}
                onChange={() => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    hasPreviousAdvice: 'no',
                    previousAdviceUploads: [] }));
                  clearErr('hasPreviousAdvice');
                  clearErr('previousAdviceUploads');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
        </div>

        {draft.hasPreviousAdvice === 'yes' ? (
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>{f.previousAdviceAttachment}</label>
            <WizardTempFileUploadList
              files={draft.previousAdviceUploads}
              uploadFile={attorneyGeneralService.uploadCaseFile}
              error={errors.previousAdviceUploads}
              onClearError={() => clearErr('previousAdviceUploads')}
              onChange={(previousAdviceUploads: AttorneyGeneralInlineUpload[]) =>
                setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, previousAdviceUploads }))
              }
              addButtonLabel={f.uploadDocuments}
            />
          </div>
        ) : null}

        <div className="ccr-fg" id="ag-connectedAdviceCase" style={{ marginBottom: 14 }}>
          <label>{f.connectedAdviceCase}</label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="connectedAdviceCase"
                checked={draft.hasConnectedAdviceCase === 'yes'}
                onChange={() => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, hasConnectedAdviceCase: 'yes' }));
                  clearErr('hasConnectedAdviceCase');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="connectedAdviceCase"
                checked={draft.hasConnectedAdviceCase === 'no'}
                onChange={() => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    hasConnectedAdviceCase: 'no',
                    connectedAdviceDetails: '' }));
                  clearErr('hasConnectedAdviceCase');
                  clearErr('connectedAdviceDetails');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
        </div>

        {draft.hasConnectedAdviceCase === 'yes' ? (
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>
              {f.connectedAdviceCaseDetails} <span className="req">*</span>
            </label>
            <textarea
              id="ag-connectedAdviceDetails"
              className={inpClass(Boolean(errors.connectedAdviceDetails))}
              rows={3}
              value={draft.connectedAdviceDetails}
              onChange={(e) => {
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  connectedAdviceDetails: maxLen('connectedAdviceDetails', e.target.value, MAX_DESC) }));
              }}
              placeholder={f.includeConnectedCaseDetails}
            />
            <FieldError message={errors.connectedAdviceDetails} />
          </div>
        ) : null}

        <div className="ccr-fg" id="ag-actionInstituted" style={{ marginBottom: 14 }}>
          <label>{f.hasActionInstituted}</label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="actionInstituted"
                checked={draft.hasActionInstituted === 'yes'}
                onChange={() => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, hasActionInstituted: 'yes' }));
                  clearErr('hasActionInstituted');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="actionInstituted"
                checked={draft.hasActionInstituted === 'no'}
                onChange={() => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    hasActionInstituted: 'no',
                    courtId: '',
                    courtName: '',
                    courtTypeId: '',
                    courtTypeName: '',
                    courtTypeOther: '' }));
                  clearErr('hasActionInstituted');
                  clearErr('courtTypeId');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
        </div>

        {showCourtType ? (
          <div className={showCourtName ? 'ccr-fr c2' : 'ccr-fg'} style={{ marginBottom: 14 }}>
            <div className="ccr-fg">
              <label>{f.courtType}</label>
              <WizardSelect
                id="ag-courtType"
                hasError={Boolean(errors.courtTypeId)}
                value={draft.courtTypeId}
                placeholder={agSelectPlaceholder('Court Type')}
                options={toOpts(lookups.courtTypes)}
                onChange={(id) => {
                  const name = lookupName(lookups.courtTypes, id);
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    courtTypeId: id,
                    courtTypeName: name,
                    courtTypeOther: '',
                    courtId: '',
                    courtName: '' }));
                  clearErr('courtTypeId');
                }}
              />
              <FieldError message={errors.courtTypeId} />
            </div>
            {showCourtName ? (
              <div className="ccr-fg">
                <label>{f.courtName}</label>
                <WizardSelect
                  id="ag-courtName"
                  value={draft.courtId}
                  disabled={!draft.courtTypeId || lookups.courtsLoading}
                  placeholder={agSelectPlaceholder('Court Name')}
                  options={toOpts(lookups.courts)}
                  onChange={(id) => {
                    setDraft((d: AttorneyGeneralWizardDraft) => ({
                      ...d,
                      courtId: id,
                      courtName: lookupName(lookups.courts, id) }));
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {showCourtOther ? (
          <div className="ccr-fg ccr-field-half" style={{ marginBottom: 14 }}>
            <label>{f.courtTypeOther}</label>
            <input
              id="ag-courtTypeOther"
              className={inpClass(Boolean(errors.courtTypeOther))}
              type="text"
              maxLength={MAX_REF}
              value={draft.courtTypeOther}
              onChange={(e) => {
                setDraft((d: AttorneyGeneralWizardDraft) => ({
                  ...d,
                  courtTypeOther: maxLen('courtTypeOther', e.target.value, MAX_REF),
                }));
                clearErr('courtTypeOther');
              }}
              placeholder={f.specifyCourtType}
            />
            <FieldError message={errors.courtTypeOther} />
          </div>
        ) : null}

        <div className="ccr-fg" id="ag-appealMatter" style={{ marginBottom: 14 }}>
          <label>{f.isAppealMatter}</label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="appealMatter"
                checked={draft.isAppealMatter === 'yes'}
                onChange={() => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, isAppealMatter: 'yes' }));
                  clearErr('isAppealMatter');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="appealMatter"
                checked={draft.isAppealMatter === 'no'}
                onChange={() => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    isAppealMatter: 'no',
                    originalCourt: '',
                    judgementOrderUploads: [] }));
                  clearErr('isAppealMatter');
                  clearErr('originalCourt');
                  clearErr('judgementOrderUploads');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
        </div>

        {draft.isAppealMatter === 'yes' ? (
          <>
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>{f.originalCourt}</label>
              <input
                id="ag-originalCourt"
                className={inpClass(Boolean(errors.originalCourt))}
                type="text"
                value={draft.originalCourt}
                onChange={(e) => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    originalCourt: maxLen('originalCourt', e.target.value, MAX_REF) }));
                }}
                placeholder={agEnterPlaceholder(f.originalCourt)}
              />
              <FieldError message={errors.originalCourt} />
            </div>
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>{f.judgementOrder}</label>
              <WizardTempFileUploadList
                files={draft.judgementOrderUploads}
                uploadFile={attorneyGeneralService.uploadCaseFile}
                error={errors.judgementOrderUploads}
                onClearError={() => clearErr('judgementOrderUploads')}
                onChange={(judgementOrderUploads: AttorneyGeneralInlineUpload[]) =>
                  setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, judgementOrderUploads }))
                }
                addButtonLabel={f.uploadDocuments}
              />
            </div>
          </>
        ) : null}

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.caseNumber}</label>
            <input
              id="ag-caseNumber"
              className={inpClass(Boolean(errors.caseNumber))}
              type="text"
              value={draft.caseNumber}
              onChange={(e) => {
                setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.courtReferenceNumber}
            />
            <FieldError message={errors.caseNumber} />
          </div>
          <div className="ccr-fg" id="ag-connected">
            <label>{f.connectedMatters}</label>
            <div className="ccr-radio-row">
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="connectedMatters"
                  checked={draft.hasConnectedMatters === 'yes'}
                  onChange={() => {
                    setDraft((d: AttorneyGeneralWizardDraft) => ({ ...d, hasConnectedMatters: 'yes' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.yes}</span>
              </label>
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="connectedMatters"
                  checked={draft.hasConnectedMatters === 'no'}
                  onChange={() => {
                    setDraft((d: AttorneyGeneralWizardDraft) => ({
                      ...d,
                      hasConnectedMatters: 'no',
                      connectedCaseNumber: '',
                      connectedAgdFileRef: '' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.no}</span>
              </label>
            </div>
          </div>
        </div>

        {draft.hasConnectedMatters === 'yes' ? (
          <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
            <div className="ccr-fg">
              <label>
                {f.connectedCaseNumber} <span className="req">*</span>
              </label>
              <input
                id="ag-connectedCase"
                className={inpClass(Boolean(errors.connectedCaseNumber))}
                type="text"
                value={draft.connectedCaseNumber}
                onChange={(e) => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    connectedCaseNumber: maxLen('connectedCaseNumber', e.target.value, MAX_REF) }));
                }}
                placeholder={f.enterConnectedCaseNumber}
              />
              <FieldError message={errors.connectedCaseNumber} />
            </div>
            <div className="ccr-fg">
              <label>
                {f.connectedAgdFileRef} <span className="req">*</span>
              </label>
              <input
                id="ag-connectedAgd"
                className={inpClass(Boolean(errors.connectedAgdFileRef))}
                type="text"
                value={draft.connectedAgdFileRef}
                onChange={(e) => {
                  setDraft((d: AttorneyGeneralWizardDraft) => ({
                    ...d,
                    connectedAgdFileRef: maxLen('connectedAgdFileRef', e.target.value, MAX_REF) }));
                }}
                placeholder={f.enterConnectedAgdFileRef}
              />
              <FieldError message={errors.connectedAgdFileRef} />
            </div>
          </div>
        ) : null}
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
